import { ElementType, HTMLAttributes, ReactNode } from "react";
import { cx } from "../../utils/cx";
import styles from "./Heading.module.scss";

export type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;

const defaultTagForLevel: Record<HeadingLevel, ElementType> = {
    1: "h1",
    2: "h2",
    3: "h3",
    4: "h4",
    5: "h5",
    6: "h6",
};

export type HeadingProps = {
    /** Visual + semantic step on the DS display ramp (1 = largest). */
    level?: HeadingLevel;
    /** Rendered element; defaults to `h{level}`. */
    as?: ElementType;
    className?: string;
    children?: ReactNode;
} & Omit<HTMLAttributes<HTMLElement>, "color">;

/**
 * Treez DS — page and section titles using the Roboto ramp (`@/theme/typography` mixins → `--ds-*`).
 * Prefer this over legacy heading classes on new screens.
 */
export function Heading({
    level = 2,
    as,
    className,
    children,
    ...rest
}: HeadingProps) {
    const Component = (as ?? defaultTagForLevel[level]) as ElementType;
    const levelClass = styles[`level${level}` as keyof typeof styles];

    return (
        <Component className={cx(styles.root, levelClass, className)} {...rest}>
            {children}
        </Component>
    );
}
